import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { mutate } from "swr";
import { serverUrl } from "../helpers/utility";
import styles from "../styles/userInfoDropdown.module.scss";

export default function UserInfoDropdown({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getDashboard = useCallback(() => {
    if (user?.staffUser) {
      return "#/admin/dashboard";
    }

    if (user?.localAuthorityId) {
      return "#/local-authority/dashboard";
    }

    return "#/provider/dashboard";
  }, [user]);

  const getUserType = () => {
    if (user?.staffUser) {
      return "Staff";
    }
    if (user?.localAuthorityId) {
      return user?.firstname || user?.lastname ? `${user?.firstname || ""} ${user?.lastname || ""}`.trim() : "Local Authority";
    }
    if (user?.careProviderOrganisationId) {
      return "Care Provider";
    }
    return "User";
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      const first = user.firstName.trim();
      const last = user.lastName.trim();
      if (first && last) {
        return `${first[0]}${last[0]}`.toUpperCase();
      }
    }
    if (user?.email) {
      const emailParts = user.email.split("@")[0].split(".");
      if (emailParts.length >= 2 && emailParts[0] && emailParts[1]) {
        return `${emailParts[0][0]}${emailParts[1][0]}`.toUpperCase();
      }
      if (user.email.length >= 2) {
        return user.email.substring(0, 2).toUpperCase();
      }
    }
    return "U";
  };

  const handleLogout = async () => {
    setIsOpen(false);
    await axios.get(`${serverUrl}/logout`);
    localStorage.removeItem("token");
    // Force SWR to revalidate and fetch fresh data
    await mutate(`${serverUrl}/user`);
    await mutate(`${serverUrl}/location`);

    setTimeout(() => {
      navigate("/login");
    }, 50);
  };

  const handleDashboardClick = () => {
    setIsOpen(false);
    window.location.href = getDashboard();
  };

  const handleTeamClick = () => {
    setIsOpen(false);
    if (user?.localAuthorityId) {
      window.location.href = "#/local-authority/dashboard/team";
    } else if (user?.careProviderOrganisationId) {
      window.location.href = "#/provider/dashboard/team";
    }
  };


  return (
    <div className={styles.userInfoDropdown} ref={dropdownRef}>
      <button
        className={styles.userInfoButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className={styles.userInfoContent}>
          {user?.avatar || user?.profileImage || user?.image ? (
            <img
              src={user.avatar || user.profileImage || user.image}
              alt="User avatar"
              className={styles.avatar}
            />
          ) : (
            <div className={styles.avatarInitials}>{getInitials()}</div>
          )}
          <div className={styles.userInfoText}>
            <div className={styles.userType}>{getUserType()}</div>
            <div className={styles.userEmail}>{user?.email || "No email"}</div>
          </div>
        </div>
        <i className={`bi bi-chevron-down ${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}></i>
      </button>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          <button
            className={styles.dropdownItem}
            onClick={handleDashboardClick}
          >
            <i className="bi bi-speedometer2"></i>
            <span>Dashboard</span>
          </button>
          {((user?.localAuthorityId && user?.role === "super_admin") || 
            (user?.careProviderOrganisationId && user?.role === "super_admin")) && (
            <button
              className={styles.dropdownItem}
              onClick={handleTeamClick}
            >
              <i className="bi bi-people"></i>
              <span>Team Management</span>
            </button>
          )}
          <button
            className={styles.dropdownItem}
            onClick={handleLogout}
          >
            <i className="bi bi-box-arrow-right"></i>
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
}

